/**
 * ToolsPanelButton — circular icon button used inside ToolsPanelGroup.
 *
 * Each button is a 48px gradient circle with an inline-SVG glyph. On
 * hover the circle scales up by ~12%. An optional badge number renders
 * a small white pill in the top-right corner — used for things like
 * pending approval count or NA log entries.
 *
 * The `icon` prop is a raw SVG inner-HTML string that gets injected via
 * `dangerouslySetInnerHTML`. The icon library lives in `icons.ts` and
 * each entry there is just the inner content of an `<svg>` element.
 */
import { useState } from 'react'

interface ToolsPanelButtonProps {
  /** Two CSS color stops, e.g. "#f59e0b,#d97706" — wrapped in linear-gradient(135deg, ...). */
  gradient: string
  /** RGB values for the box-shadow tint, e.g. "245,158,11". */
  shadow: string
  /** Raw SVG inner-HTML string. */
  icon: string
  label: string
  badge?: number
  onClick: () => void
}

export default function ToolsPanelButton({
  gradient,
  shadow,
  icon,
  label,
  badge,
  onClick,
}: ToolsPanelButtonProps) {
  const [hov, setHov] = useState(false)

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer',
      }}
    >
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: `linear-gradient(135deg,${gradient})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 16px rgba(${shadow},0.45)`,
          transform: hov ? 'scale(1.12)' : 'scale(1)',
          transition: 'transform 0.18s',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          dangerouslySetInnerHTML={{ __html: icon }}
        />
        {badge != null && badge > 0 && (
          <div
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              background: '#fff',
              color: '#dc2626',
              width: 18,
              height: 18,
              borderRadius: '50%',
              fontSize: 10,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {badge}
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: 9,
          color: 'var(--text-muted)',
          textAlign: 'center',
          fontWeight: 600,
          lineHeight: 1.2,
          maxWidth: 76,
        }}
      >
        {label}
      </div>
    </div>
  )
}
